import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { Contact, getAvatarColor } from "./types";
import EditContactModal from "./EditContactModal";
import DeleteConfirmDialog from "./DeleteConfirmDialog";
import Avatar, { useAvatarPreloader } from "./Avatar";

interface ContactsViewProps {
  accessToken: string;
  onSendEmail?: (selectedContacts: Contact[]) => void;
  clearSelectionTrigger?: number; // Increment to clear selection from parent
}

// Extract company from notes
function extractCompany(notes: string | null): string | null {
  if (!notes) return null;
  const match = notes.match(/Company:\s*([^\n]+)/i);
  return match ? match[1].trim() : null;
}

// Get initials for avatar
function getInitials(contact: Contact): string {
  const first = contact.first_name?.[0] || "";
  const last = contact.last_name?.[0] || "";
  return (first + last).toUpperCase() || "?";
}

// Clean notes for display
function cleanNotes(notes: string | null): string | null {
  if (!notes) return null;
  return (
    notes
      .split("\n")
      .filter(
        (line) =>
          !line.match(/^Company:/i) &&
          !line.match(/^Type:/i) &&
          !line.match(/^Priority:/i) &&
          !line.match(/^\[From/)
      )
      .join(" · ")
      .trim() || null
  );
}

// Get all unique sources from contacts
function getAllSources(contacts: Contact[]): string[] {
  const sources = new Set<string>();
  contacts.forEach((c) => {
    if (c.is_valid) {
      c.source_boards.forEach((s) => sources.add(s));
    }
  });
  return Array.from(sources).sort();
}

// Multi-select dropdown component
interface SourceDropdownProps {
  allSources: string[];
  selectedSources: Set<string>;
  onToggleSource: (source: string) => void;
  onClearAll: () => void;
  onSelectAll: (sources: string[]) => void;
}

const SourceDropdown: React.FC<SourceDropdownProps> = ({
  allSources,
  selectedSources,
  onToggleSource,
  onClearAll,
  onSelectAll,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const filteredSources = allSources.filter((s) =>
    s.toLowerCase().includes(search.toLowerCase())
  );

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectedCount = selectedSources.size;
  const buttonLabel =
    selectedCount === 0
      ? "All sources"
      : selectedCount === 1
        ? Array.from(selectedSources)[0].split("/").pop()
        : `${selectedCount} sources selected`;

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-4 py-2.5 rounded bg-navy/80 border border-border-subtle text-sm text-text-primary hover:border-border-strong transition-all duration-200"
      >
        <svg
          className="w-4 h-4 text-text-muted"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
          />
        </svg>
        <span className="max-w-[200px] truncate">{buttonLabel}</span>
        <svg
          className={`w-4 h-4 text-text-muted transition-transform ${isOpen ? "rotate-180" : ""}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute top-full right-0 mt-2 w-80 max-h-[480px] overflow-hidden rounded bg-navy border border-border-strong shadow-xl z-50">
          <div className="flex items-center justify-between px-4 py-3 border-b border-border-strong bg-navy/90">
            <span className="text-xs text-text-muted uppercase tracking-wider">
              Filter by source
            </span>
            <div className="flex gap-2">
              <button
                onClick={() => onSelectAll(filteredSources)}
                className="text-xs text-rose-accent hover:text-rose-bright transition-colors"
              >
                Select all ({filteredSources.length})
              </button>
              <span className="text-text-muted">·</span>
              <button
                onClick={onClearAll}
                className="text-xs text-text-secondary hover:text-text-primary transition-colors"
              >
                Clear
              </button>
            </div>
          </div>

          <div className="px-3 py-2 border-b border-border-strong">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search sources..."
              className="w-full px-3 py-1.5 text-sm bg-navy-mid border border-border-subtle rounded text-text-primary placeholder-text-muted focus:outline-none focus:border-border-strong"
              autoFocus
            />
          </div>

          <div className="overflow-y-auto max-h-72 py-2">
            {filteredSources.map((source) => {
              const isSelected = selectedSources.has(source);
              const shortName = source.split("/").pop() || source;
              const prefix = source.includes("/") ? source.split("/")[0] : null;

              return (
                <button
                  key={source}
                  onClick={() => onToggleSource(source)}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-navy-mid/50 transition-colors ${
                    isSelected ? "bg-rose-glow" : ""
                  }`}
                >
                  <div
                    className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${
                      isSelected
                        ? "bg-rose-accent border-rose-accent"
                        : "border-border-strong hover:border-border-strong"
                    }`}
                  >
                    {isSelected && (
                      <svg
                        className="w-3 h-3 text-white"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={3}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className={`text-sm ${isSelected ? "text-white" : "text-text-primary"}`}>
                      {shortName}
                    </span>
                    {prefix && <span className="text-xs text-text-muted ml-2">({prefix})</span>}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

// Contact row component
interface ContactRowProps {
  contact: Contact;
  onEdit: (contact: Contact) => void;
  onDelete: (contact: Contact) => void;
  isSelected: boolean;
  onToggleSelect: (contact: Contact) => void;
  isSelectable: boolean; // False if unsubscribed or no email
}

const ContactRow: React.FC<ContactRowProps> = ({
  contact,
  onEdit,
  onDelete,
  isSelected,
  onToggleSelect,
  isSelectable,
}) => {
  const company = extractCompany(contact.notes);
  const initials = getInitials(contact);
  const avatarColor = getAvatarColor(contact.full_name);
  const notes = cleanNotes(contact.notes);

  const address = [contact.suburb, contact.state].filter(Boolean).join(", ");

  return (
    <div
      className={`group flex items-center gap-4 px-4 py-3 hover:bg-navy/50 border-b border-navy/50 transition-colors ${isSelected ? "bg-rose-glow" : ""}`}
    >
      {/* Selection Checkbox */}
      <div className="flex-shrink-0">
        <button
          onClick={() => isSelectable && onToggleSelect(contact)}
          disabled={!isSelectable}
          className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${
            isSelected
              ? "bg-rose-accent border-rose-accent"
              : isSelectable
                ? "border-border-strong hover:border-border-strong"
                : "border-border-strong bg-navy/50 cursor-not-allowed"
          }`}
          title={
            !isSelectable
              ? contact.is_bounced
                ? "Email bounced - cannot send"
                : contact.is_unsubscribed
                  ? "Contact is unsubscribed"
                  : "Contact has no email"
              : isSelected
                ? "Deselect"
                : "Select"
          }
        >
          {isSelected && (
            <svg
              className="w-3 h-3 text-white"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={3}
                d="M5 13l4 4L19 7"
              />
            </svg>
          )}
          {!isSelectable && (contact.is_unsubscribed || contact.is_bounced) && (
            <svg
              className="w-3 h-3 text-border-strong"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"
              />
            </svg>
          )}
        </button>
      </div>
      <Avatar
        email={contact.email}
        name={contact.full_name}
        profilePhoto={contact.profile_photo}
        size={40}
        className="flex-shrink-0 rounded-sm"
        fallbackClassName={`flex-shrink-0 rounded-sm bg-gradient-to-br ${avatarColor}`}
        fallbackContent={<span className="text-white text-sm font-medium">{initials}</span>}
      />

      <div className="w-48 flex-shrink-0">
        <p className="text-sm font-medium text-white truncate">
          {contact.title && <span className="text-text-muted">{contact.title} </span>}
          {contact.full_name || "Unknown"}
        </p>
        {company && <p className="text-xs text-text-muted truncate">{company}</p>}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
          {contact.email && (
            <a
              href={`mailto:${contact.email}`}
              className="text-sm text-rose-accent hover:text-rose-bright transition-colors truncate max-w-[220px]"
            >
              {contact.email}
            </a>
          )}
          {contact.mobile_phone && (
            <a
              href={`tel:${contact.mobile_phone}`}
              className="text-sm text-text-secondary hover:text-white transition-colors"
            >
              {contact.mobile_phone}
            </a>
          )}
          {contact.home_phone && (
            <span className="text-sm text-text-muted">
              H:{" "}
              <a href={`tel:${contact.home_phone}`} className="hover:text-white transition-colors">
                {contact.home_phone}
              </a>
            </span>
          )}
          {contact.work_phone && (
            <span className="text-sm text-text-muted">
              W:{" "}
              <a href={`tel:${contact.work_phone}`} className="hover:text-white transition-colors">
                {contact.work_phone}
              </a>
            </span>
          )}
          {address && <span className="text-sm text-text-muted">{address}</span>}
        </div>
        {notes && <p className="text-xs text-text-muted truncate mt-1">{notes}</p>}
      </div>

      <div className="flex-shrink-0 flex gap-1 mr-2">
        {contact.source_boards.slice(0, 2).map((board, i) => (
          <span key={i} className="px-2 py-0.5 text-xs bg-navy text-text-muted rounded">
            {board.split("/").pop()}
          </span>
        ))}
        {contact.source_boards.length > 2 && (
          <span className="px-2 py-0.5 text-xs text-text-muted">
            +{contact.source_boards.length - 2}
          </span>
        )}
      </div>

      <div className="flex-shrink-0 flex gap-1.5">
        <button
          onClick={() => onEdit(contact)}
          className="p-2 rounded-sm bg-navy-mid/50 text-text-secondary hover:text-rose-accent hover:bg-rose-glow transition-colors"
          title="Edit contact"
        >
          <svg
            className="w-4 h-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10"
            />
          </svg>
        </button>
        <button
          onClick={() => onDelete(contact)}
          className="p-2 rounded-sm bg-navy-mid/50 text-text-secondary hover:text-red-400 hover:bg-red-500/10 transition-colors"
          title="Delete contact"
        >
          <svg
            className="w-4 h-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
            />
          </svg>
        </button>
      </div>
    </div>
  );
};

// Loading skeleton component
const LoadingSkeleton: React.FC = () => (
  <div className="flex flex-col h-full">
    <div className="flex flex-col sm:flex-row gap-3 pb-4">
      <div className="flex-1 h-11 bg-navy/50 rounded animate-pulse" />
      <div className="w-40 h-11 bg-navy/50 rounded animate-pulse" />
      <div className="w-32 h-11 bg-navy/50 rounded animate-pulse" />
    </div>
    <div className="h-4 w-24 bg-navy/50 rounded mb-2 animate-pulse" />
    <div className="flex-1 min-h-0 rounded bg-navy-deeper/50 border border-navy p-4">
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="inline-block w-8 h-8 border-2 border-rose-accent/30 border-t-rose-accent rounded-full animate-spin mb-3" />
          <p className="text-text-secondary text-sm">Loading contacts...</p>
        </div>
      </div>
    </div>
  </div>
);

// Error display component with diagnostic info
const ErrorDisplay: React.FC<{ error: string; details?: string; onRetry: () => void }> = ({
  error,
  details,
  onRetry,
}) => (
  <div className="flex flex-col h-full">
    <div className="flex-1 min-h-0 rounded bg-navy-deeper/50 border border-red-900/50 p-4">
      <div className="flex items-center justify-center h-full">
        <div className="text-center max-w-md">
          <svg
            className="w-12 h-12 mx-auto text-red-500/50 mb-3"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          <p className="text-red-400 text-sm mb-2">Failed to load contacts</p>
          <p className="text-text-muted text-xs mb-2">{error}</p>
          {details && (
            <pre className="text-text-muted text-xs mb-2 font-mono bg-navy/50 p-2 rounded text-left whitespace-pre-wrap break-all">
              {details}
            </pre>
          )}
          <button
            onClick={onRetry}
            className="mt-2 px-4 py-2 bg-navy-mid hover:bg-navy-mid/80 text-white text-sm rounded-sm transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    </div>
  </div>
);

// Toast notification component
interface ToastProps {
  message: string;
  type: "error" | "success";
  onClose: () => void;
}

const Toast: React.FC<ToastProps> = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 5000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className="fixed bottom-20 right-4 z-50 animate-in slide-in-from-bottom-2 fade-in duration-200">
      <div
        className={`flex items-center gap-3 px-4 py-3 rounded shadow-lg border ${
          type === "error"
            ? "bg-red-900/90 border-red-800 text-red-100"
            : "bg-emerald-900/90 border-emerald-800 text-emerald-100"
        }`}
      >
        <div
          className={`flex-shrink-0 w-5 h-5 ${
            type === "error" ? "text-red-400" : "text-emerald-400"
          }`}
        >
          {type === "error" ? (
            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          ) : (
            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          )}
        </div>
        <p className="text-sm font-medium">{message}</p>
        <button
          onClick={onClose}
          className={`flex-shrink-0 p-1 rounded-sm transition-colors ${
            type === "error" ? "hover:bg-red-800" : "hover:bg-emerald-800"
          }`}
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>
    </div>
  );
};

// Debounce hook
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);

  return debouncedValue;
}

const ContactsView: React.FC<ContactsViewProps> = ({
  accessToken,
  onSendEmail,
  clearSelectionTrigger,
}) => {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [errorDetails, setErrorDetails] = useState<string | null>(null);

  const [nameSearchQuery, setNameSearchQuery] = useState("");
  const [otherFieldsSearchQuery, setOtherFieldsSearchQuery] = useState("");
  const debouncedNameSearch = useDebounce(nameSearchQuery, 200);
  const debouncedOtherFieldsSearch = useDebounce(otherFieldsSearchQuery, 200);
  const [selectedSources, setSelectedSources] = useState<Set<string>>(new Set());

  // Edit/Delete state
  const [editingContact, setEditingContact] = useState<Contact | null>(null);
  const [deletingContact, setDeletingContact] = useState<Contact | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Contact selection state for bulk email
  const [selectedContactIds, setSelectedContactIds] = useState<Set<string>>(new Set());

  // Toast notification state
  const [toast, setToast] = useState<{ message: string; type: "error" | "success" } | null>(null);


  // Helper to check if a contact can be selected for email
  const isContactSelectable = useCallback((contact: Contact): boolean => {
    return Boolean(contact.email) && !contact.is_unsubscribed && !contact.is_bounced;
  }, []);

  // Clear selection when trigger changes (from parent after successful email send)
  useEffect(() => {
    if (clearSelectionTrigger !== undefined && clearSelectionTrigger > 0) {
      setSelectedContactIds(new Set());
    }
  }, [clearSelectionTrigger]);

  // Fetch contacts from API
  const fetchContacts = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    setErrorDetails(null);

    try {
      const response = await fetch("/api/contacts", {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        // Try to extract error details from response
        let serverError = "";
        try {
          const errorData = await response.json();
          // Format structured error with diagnostic info
          if (errorData.code || errorData.path) {
            const parts = [];
            if (errorData.code) parts.push(`Code: ${errorData.code}`);
            if (errorData.path) parts.push(`Path: ${errorData.path}`);
            if (errorData.cwd) parts.push(`CWD: ${errorData.cwd}`);
            if (errorData.detail) parts.push(`Detail: ${errorData.detail}`);
            serverError = parts.join("\n");
          } else {
            serverError = errorData.error || errorData.message || JSON.stringify(errorData);
          }
        } catch {
          // Response wasn't JSON, try text
          try {
            serverError = await response.text();
          } catch {
            serverError = "Could not read error response";
          }
        }

        if (response.status === 401) {
          setErrorDetails(serverError);
          throw new Error("Authentication expired. Please sign in again.");
        }

        setErrorDetails(serverError);
        throw new Error(`Failed to load contacts (${response.status})`);
      }

      const data: Contact[] = await response.json();
      // Deduplicate by contact_id (keep first occurrence)
      const seen = new Set<string>();
      const dedupedContacts = data.filter((c) => {
        if (seen.has(c.contact_id)) {
          console.warn(`[Contacts] Duplicate contact_id found: ${c.contact_id} (${c.full_name})`);
          return false;
        }
        seen.add(c.contact_id);
        return true;
      });
      setContacts(dedupedContacts);
    } catch (err) {
      setError(err instanceof Error ? err.message : "An unexpected error occurred");
    } finally {
      setIsLoading(false);
    }
  }, [accessToken]);

  // Initial fetch
  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  // Preload avatars in background
  const contactEmails = useMemo(() => contacts.map((c) => c.email), [contacts]);
  useAvatarPreloader(contactEmails, accessToken);

  const allSources = useMemo(() => getAllSources(contacts), [contacts]);

  const filteredContacts = useMemo(() => {
    return contacts.filter((c) => {
      if (!c.is_valid) return false;

      // Source filter
      if (selectedSources.size > 0) {
        const hasMatchingSource = c.source_boards.some((s) => selectedSources.has(s));
        if (!hasMatchingSource) return false;
      }

      // Name search filter
      if (debouncedNameSearch.trim()) {
        const query = debouncedNameSearch.toLowerCase();
        const nameFields = [c.title, c.first_name, c.last_name, c.full_name];
        if (!nameFields.some((field) => field?.toLowerCase().includes(query))) {
          return false;
        }
      }

      // Other fields search filter
      if (debouncedOtherFieldsSearch.trim()) {
        const query = debouncedOtherFieldsSearch.toLowerCase();
        const otherFields = [
          c.email,
          c.mobile_phone,
          c.home_phone,
          c.work_phone,
          c.notes,
          c.suburb,
          c.state,
          c.street_address,
        ];
        if (!otherFields.some((field) => field?.toLowerCase().includes(query))) {
          return false;
        }
      }

      return true;
    });
  }, [contacts, debouncedNameSearch, debouncedOtherFieldsSearch, selectedSources]);

  const handleToggleSource = (source: string) => {
    setSelectedSources((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(source)) {
        newSet.delete(source);
      } else {
        newSet.add(source);
      }
      return newSet;
    });
  };

  const handleClearAllSources = () => {
    setSelectedSources(new Set());
  };

  const handleSelectAllSources = (sources: string[]) => {
    setSelectedSources((prev) => new Set([...prev, ...sources]));
  };

  // Contact selection handlers for bulk email
  const handleToggleContactSelect = (contact: Contact) => {
    setSelectedContactIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(contact.contact_id)) {
        newSet.delete(contact.contact_id);
      } else {
        newSet.add(contact.contact_id);
      }
      return newSet;
    });
  };

  const handleSelectAllContacts = () => {
    const selectableContacts = filteredContacts.filter(isContactSelectable);
    setSelectedContactIds(new Set(selectableContacts.map((c) => c.contact_id)));
  };

  const handleClearContactSelection = () => {
    setSelectedContactIds(new Set());
  };

  // Get selected contacts for email
  const selectedContacts = useMemo(() => {
    return contacts.filter((c) => selectedContactIds.has(c.contact_id));
  }, [contacts, selectedContactIds]);

  // Count of selectable contacts in current filtered view
  const selectableContactsCount = useMemo(() => {
    return filteredContacts.filter(isContactSelectable).length;
  }, [filteredContacts, isContactSelectable]);

  // Edit contact handler
  const handleEditContact = (contact: Contact) => {
    setEditingContact(contact);
  };

  // Save contact handler
  const handleSaveContact = async (updates: Partial<Contact>) => {
    if (!editingContact) return;

    setIsSaving(true);
    try {
      const response = await fetch(`/api/contacts/${editingContact.contact_id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(updates),
      });

      if (!response.ok) {
        throw new Error("Failed to update contact");
      }

      const updatedContact = await response.json();

      // Update the contact in the local state
      setContacts((prev) =>
        prev.map((c) => (c.contact_id === updatedContact.contact_id ? updatedContact : c))
      );

      setEditingContact(null);
    } catch (err) {
      console.error("Error updating contact:", err);
      setToast({ message: "Failed to update contact. Please try again.", type: "error" });
    } finally {
      setIsSaving(false);
    }
  };

  // Delete contact handler
  const handleDeleteContact = (contact: Contact) => {
    setDeletingContact(contact);
  };

  // Confirm delete handler
  const handleConfirmDelete = async () => {
    if (!deletingContact) return;

    setIsDeleting(true);
    try {
      const response = await fetch(`/api/contacts/${deletingContact.contact_id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to delete contact");
      }

      // Remove the contact from local state
      setContacts((prev) => prev.filter((c) => c.contact_id !== deletingContact.contact_id));

      setDeletingContact(null);
    } catch (err) {
      console.error("Error deleting contact:", err);
      setToast({ message: "Failed to delete contact. Please try again.", type: "error" });
    } finally {
      setIsDeleting(false);
    }
  };

  if (isLoading) {
    return <LoadingSkeleton />;
  }

  if (error) {
    return (
      <ErrorDisplay error={error} details={errorDetails ?? undefined} onRetry={fetchContacts} />
    );
  }

  const totalValidContacts = contacts.filter((c) => c.is_valid).length;

  return (
    <div className="flex flex-col h-full relative">
      <div className="flex flex-col sm:flex-row gap-3 pb-4">
        {/* Name Search Box */}
        <div className="relative flex-1">
          <div className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none">
            <svg
              className="w-4 h-4 text-text-muted"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
              />
            </svg>
          </div>
          <input
            type="text"
            value={nameSearchQuery}
            onChange={(e) => setNameSearchQuery(e.target.value)}
            placeholder="Search by name..."
            className="w-full pl-11 pr-4 py-2.5 rounded bg-navy/80 border border-border-subtle text-white text-sm placeholder-text-muted focus:outline-none focus:border-rose-accent/40 focus:ring-2 focus:ring-rose-accent/15 transition-all"
          />
          {nameSearchQuery && (
            <button
              onClick={() => setNameSearchQuery("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-navy-mid transition-colors"
            >
              <svg
                className="w-3 h-3 text-text-muted"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        {/* Other Fields Search Box */}
        <div className="relative flex-1">
          <div className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none">
            <svg
              className="w-4 h-4 text-text-muted"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>
          <input
            type="text"
            value={otherFieldsSearchQuery}
            onChange={(e) => setOtherFieldsSearchQuery(e.target.value)}
            placeholder="Search email, phone, address, other..."
            className="w-full pl-11 pr-4 py-2.5 rounded bg-navy/80 border border-border-subtle text-white text-sm placeholder-text-muted focus:outline-none focus:border-rose-accent/40 focus:ring-2 focus:ring-rose-accent/15 transition-all"
          />
          {otherFieldsSearchQuery && (
            <button
              onClick={() => setOtherFieldsSearchQuery("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-navy-mid transition-colors"
            >
              <svg
                className="w-3 h-3 text-text-muted"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        <SourceDropdown
          allSources={allSources}
          selectedSources={selectedSources}
          onToggleSource={handleToggleSource}
          onClearAll={handleClearAllSources}
          onSelectAll={handleSelectAllSources}
        />
      </div>

      {/* Contact count and selection controls */}
      <div className="flex items-center justify-between pb-2">
        <p className="text-xs text-text-muted">
          {filteredContacts.length === totalValidContacts
            ? `${filteredContacts.length} contacts`
            : `${filteredContacts.length} of ${totalValidContacts} contacts`}
          {selectedContactIds.size > 0 && (
            <span className="text-rose-accent ml-2">({selectedContactIds.size} selected)</span>
          )}
        </p>
        {selectableContactsCount > 0 && (
          <div className="flex items-center gap-2">
            <button
              onClick={handleSelectAllContacts}
              className="text-xs text-rose-accent hover:text-rose-bright transition-colors"
            >
              Select all ({selectableContactsCount} valid)
            </button>
            {selectedContactIds.size > 0 && (
              <>
                <span className="text-text-muted">·</span>
                <button
                  onClick={handleClearContactSelection}
                  className="text-xs text-text-secondary hover:text-text-primary transition-colors"
                >
                  Clear selection
                </button>
              </>
            )}
          </div>
        )}
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto rounded bg-navy-deeper/50 border border-navy scrollbar-thin scrollbar-thumb-navy-mid scrollbar-track-transparent">
        {filteredContacts.length === 0 ? (
          <div className="text-center py-12">
            <svg
              className="w-12 h-12 mx-auto text-navy-mid mb-3"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1}
                d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
              />
            </svg>
            <p className="text-text-muted text-sm">No contacts found</p>
            <p className="text-text-muted text-xs mt-1">Try adjusting your search or filters</p>
          </div>
        ) : (
          filteredContacts.map((contact) => (
            <ContactRow
              key={contact.contact_id}
              contact={contact}
              onEdit={handleEditContact}
              onDelete={handleDeleteContact}
              isSelected={selectedContactIds.has(contact.contact_id)}
              onToggleSelect={handleToggleContactSelect}
              isSelectable={isContactSelectable(contact)}
            />
          ))
        )}
      </div>

      {/* Edit Contact Modal */}
      {editingContact && (
        <EditContactModal
          contact={editingContact}
          onSave={handleSaveContact}
          onCancel={() => setEditingContact(null)}
          isSaving={isSaving}
          allSources={allSources}
        />
      )}

      {/* Delete Confirmation Dialog */}
      {deletingContact && (
        <DeleteConfirmDialog
          contact={deletingContact}
          onConfirm={handleConfirmDelete}
          onCancel={() => setDeletingContact(null)}
          isDeleting={isDeleting}
        />
      )}

      {/* Floating Action Bar for Send Email */}
      {selectedContactIds.size > 0 && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-40 animate-in slide-in-from-bottom-4 fade-in duration-200">
          <div className="flex items-center gap-3 px-4 py-3 rounded bg-navy/95 border border-border-strong shadow-xl backdrop-blur-sm">
            <div className="text-sm text-text-primary">
              <span className="font-medium text-white">{selectedContactIds.size}</span> contact
              {selectedContactIds.size !== 1 ? "s" : ""} selected
            </div>
            <div className="w-px h-6 bg-border-strong" />
            <button
              onClick={handleClearContactSelection}
              className="px-3 py-1.5 text-sm text-text-secondary hover:text-text-primary transition-colors"
            >
              Clear
            </button>
            <button
              onClick={() => onSendEmail?.(selectedContacts)}
              className="flex items-center gap-2 px-4 py-2 rounded bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-medium transition-colors shadow-lg shadow-status-success/20"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
              Send Email ({selectedContactIds.size})
            </button>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
};

export default ContactsView;
